import type { Optional } from '@festina-life/core'
import {
  CharStreams,
  CommonTokenStream,
  ErrorListener,
  type Token,
} from 'antlr4'

import FPlusLexer from '../lexer/ANTLR/FPlusLexer'
import FPlusParser, {
  type ExpressionScriptContext,
  type FunctionScriptContext,
} from '../lexer/ANTLR/FPlusParser'
import { FunctionExtractor, type FunctionMetadata } from './FunctionExtractor'

class OffendingTokenCollector extends ErrorListener<Token> {
  readonly offendingTokenIndexes: number[] = []

  override syntaxError(
    _recognizer: unknown,
    offendingSymbol: Optional<Token>,
  ): void {
    if (offendingSymbol) {
      this.offendingTokenIndexes.push(offendingSymbol.tokenIndex)
    }
  }
}

const parseFunctionScriptWithDiagnostics = (code: string) => {
  const input = CharStreams.fromString(code)
  const lexer = new FPlusLexer(input)
  const tokens = new CommonTokenStream(lexer)
  const parser = new FPlusParser(tokens)

  // The parser is extremely verbose and we don't want to see the errors in the console
  //  Given that it still manages to parse the code when errors are reported, we can safely remove the error listeners
  parser.removeErrorListeners()

  const errorCollector = new OffendingTokenCollector()
  parser.addErrorListener(errorCollector)

  const ast = parser.functionScript()

  return { ast, offendingTokenIndexes: errorCollector.offendingTokenIndexes }
}

export function parseFPlusFunctionScript(code: string): FunctionScriptContext {
  return parseFunctionScriptWithDiagnostics(code).ast
}

/**
 * The grammar defines `functionScript` as `functionDefinition '=' expressionScript`,
 * so a script that starts with anything else - a `let ... in ...` expression, for
 * instance - is not a function. Because the parser is deliberately error tolerant it
 * recovers from such a prefix by dropping the offending tokens, which leaves the first
 * `let` binding looking like a perfectly valid declaration.
 *
 * Errors in the *body* must stay tolerated (that is what keeps half written functions
 * parseable), so we only reject a declaration that itself needed error recovery.
 */
const declarationNeededErrorRecovery = (
  ast: FunctionScriptContext,
  offendingTokenIndexes: number[],
) => {
  const declarationEndIndex = ast.functionDefinition()?.stop?.tokenIndex

  if (declarationEndIndex === undefined || declarationEndIndex === null) {
    return false
  }

  return offendingTokenIndexes.some((index) => index <= declarationEndIndex)
}

export function extractAllFunctions(
  code: Optional<string>,
): FunctionMetadata[] {
  if (!code) {
    return []
  }

  const { ast, offendingTokenIndexes } =
    parseFunctionScriptWithDiagnostics(code)

  if (declarationNeededErrorRecovery(ast, offendingTokenIndexes)) {
    return []
  }

  const extractor = new FunctionExtractor()
  extractor.setOriginalInput(code)
  return extractor.visit(ast)
}

export function extractFunction(
  code: Optional<string>,
): Optional<FunctionMetadata> {
  if (!code) {
    return undefined
  }

  const result = extractAllFunctions(code)

  if (result.length > 1) {
    throw new Error('Multiple functions found in the code')
  }

  return result[0]
}

export function parseFPlusExpressionScript(
  code: string,
): ExpressionScriptContext {
  const input = CharStreams.fromString(code)
  const lexer = new FPlusLexer(input)
  const tokens = new CommonTokenStream(lexer)
  const parser = new FPlusParser(tokens)

  parser.removeErrorListeners()

  return parser.expressionScript()
}
